import { useState, useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ChevronDown,
  ChevronRight,
  ListTree,
  ChevronsUpDown,
  ChevronsDownUp,
} from "lucide-react";
import type { VisualSeed, VisualNode } from "@/hooks/useMapVisualData";
import { ROLE_COLORS, ROLE_LABELS } from "./constants";

interface BriefCardsProps {
  seeds: VisualSeed[];
  nodes: VisualNode[];
}

export function BriefCards({ seeds, nodes }: BriefCardsProps) {
  // Trzymamy zwinięte (NIE rozwinięte) — domyślnie pusta sekwencja = WSZYSTKIE rozwinięte.
  // User chce widzieć całą strukturę H2/H3 od razu (sprzedażowy efekt jak w renault/growthhacker).
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(new Set());

  const briefNodes = useMemo(
    () =>
      nodes.filter(
        (n) =>
          n.outline &&
          n.outline.length > 0 &&
          [
            "outline_ready",
            "draft",
            "optimization",
            "humanization",
            "ready",
            "published",
          ].includes(n.engine_stage || ""),
      ),
    [nodes],
  );

  if (briefNodes.length === 0) {
    return (
      <div className="border-2 border-dashed rounded-xl p-12 text-center">
        <ListTree className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" />
        <p className="text-muted-foreground">Brak outline'ow — wygeneruj struktury w Content Engine</p>
      </div>
    );
  }

  const seedNameMap = new Map(seeds.map(s => [s.id, s.name]));
  const allCollapsed = briefNodes.every((n) => collapsedIds.has(n.id));

  const toggle = (id: string) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (allCollapsed) {
      setCollapsedIds(new Set());
    } else {
      setCollapsedIds(new Set(briefNodes.map((n) => n.id)));
    }
  };

  return (
    <div className="space-y-3">
      {/* Toolbar: globalne zwijanie/rozwijanie */}
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          {briefNodes.length} brief
          {briefNodes.length === 1 ? "" : briefNodes.length < 5 ? "y" : "ów"}
          {!allCollapsed &&
            collapsedIds.size > 0 &&
            ` · ${briefNodes.length - collapsedIds.size} rozwiniętych`}
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={toggleAll}
          className="h-7 text-xs"
        >
          {allCollapsed ? (
            <>
              <ChevronsUpDown className="h-3.5 w-3.5 mr-1" /> Rozwiń wszystkie
            </>
          ) : (
            <>
              <ChevronsDownUp className="h-3.5 w-3.5 mr-1" /> Zwiń wszystkie
            </>
          )}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {briefNodes.map(node => {
        const isExpanded = !collapsedIds.has(node.id);
        const h2Sections = (node.outline || []).filter(s => s.type === "h2" && s.header);
        const h2Count = h2Sections.length;
        const h3Count = h2Sections.reduce((sum, s) => sum + (s.h3s?.length || 0), 0);

        return (
          <div
            key={node.id}
            className="rounded-xl border bg-card hover:border-violet-500/50 transition-colors cursor-pointer"
            onClick={() => toggle(node.id)}
          >
            {/* Header */}
            <div className="p-4 pb-2">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-sm leading-tight truncate">{node.title}</h3>
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">{node.primary_entity_raw}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Badge variant="outline" className="text-[10px] px-1.5" style={{ borderColor: ROLE_COLORS[node.document_role], color: ROLE_COLORS[node.document_role] }}>
                    {ROLE_LABELS[node.document_role] || node.document_role}
                  </Badge>
                  {isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                </div>
              </div>
              <div className="flex items-center gap-3 mt-2 text-[10px] text-muted-foreground">
                <span>{seedNameMap.get(node.seed_id) || "?"}</span>
                <span>{h2Count} H2</span>
                <span>{h3Count} H3</span>
                {node.quality_score != null && (
                  <span className={node.quality_score >= 70 ? "text-green-400" : node.quality_score >= 40 ? "text-amber-400" : "text-red-400"}>
                    QA: {node.quality_score}
                  </span>
                )}
              </div>
            </div>

            {/* Compact outline (always visible) */}
            <div className="px-4 pb-2">
              <div className="flex flex-wrap gap-1">
                {h2Sections.slice(0, isExpanded ? 999 : 4).map((s, i) => (
                  <Badge key={i} variant="secondary" className="text-[10px] px-1.5 py-0 bg-violet-500/10 text-violet-400 border-0">
                    {(s.header || "").length > 30 ? (s.header || "").slice(0, 27) + "..." : s.header}
                  </Badge>
                ))}
                {!isExpanded && h2Sections.length > 4 && (
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0">+{h2Sections.length - 4}</Badge>
                )}
              </div>
            </div>

            {/* Expanded outline tree */}
            {isExpanded && (
              <div className="px-4 pb-4 pt-2 border-t border-border/50">
                <div className="space-y-1.5">
                  {h2Sections.map((section, i) => (
                    <div key={i}>
                      <div className="flex items-center gap-2">
                        <span className="shrink-0 px-1 py-0.5 rounded text-[9px] font-bold bg-violet-500/20 text-violet-300">H2</span>
                        <span className="text-xs font-medium">{section.header}</span>
                      </div>
                      {section.h3s && section.h3s.length > 0 && (
                        <div className="ml-7 mt-0.5 space-y-0.5">
                          {section.h3s.map((h3, j) => (
                            <div key={j} className="flex items-center gap-2">
                              <span className="shrink-0 px-1 py-0.5 rounded text-[9px] font-bold bg-blue-500/15 text-blue-400">H3</span>
                              <span className="text-xs text-muted-foreground">{h3.header}</span>
                              {h3.source_paa && (
                                <Badge variant="outline" className="text-[8px] px-1 py-0 border-amber-500/30 text-amber-500">PAA</Badge>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
      </div>
    </div>
  );
}
